import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { dataAPI, exportAPI } from '../lib/api';
import {
  TrendingUp, AlertTriangle, Target, BookOpen, ArrowRight, ExternalLink,
  Clock, Zap, Rocket, Brain, ChevronDown, ChevronUp, Link2, Download, Loader2,
  BarChart3, CheckCircle, Circle, ArrowUpRight, Layers, Map, GraduationCap,
  Flag, Star, ChevronRight
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Cell
} from 'recharts';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'strengths', label: 'Strengths', icon: TrendingUp },
  { id: 'gaps', label: 'Gap Analysis', icon: AlertTriangle },
  { id: 'roadmap', label: 'Learning Roadmap', icon: Map },
  { id: 'actions', label: 'Action Plan', icon: Target },
  { id: 'future', label: 'Future Skills', icon: Rocket },
];

const STATUS_CONFIG = {
  excellent: { color: '#16A34A', label: 'Excellent', bg: 'rgba(22,163,106,0.08)' },
  strong: { color: '#3B82F6', label: 'Strong', bg: 'rgba(59,130,246,0.08)' },
  critical: { color: '#DC2626', label: 'Critical', bg: 'rgba(220,38,38,0.08)' },
  needs_improvement: { color: '#F5841F', label: 'Needs Work', bg: 'rgba(245,132,31,0.08)' },
};

// Animated circular progress ring
function ProgressRing({ value, size = 80, stroke = 6, color = '#0D9488' }) {
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (value / 100) * circ;
  const fontSize = size <= 56 ? 10 : size <= 80 ? 14 : 18;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="var(--t-border)" strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-black" style={{ fontSize, color }}>{value}%</span>
      </div>
    </div>
  );
}

// Sidebar nav for the page
// --- OVERVIEW SECTION ---
function OverviewSection({ data }) {
  const allItems = [
    ...data.strengths.map(s => ({ ...s, type: 'strength' })),
    ...data.gaps.map(g => ({ ...g, type: 'gap' })),
  ].sort((a, b) => b.level - a.level);

  const radarData = allItems.map(item => ({
    dim: item.skill.split(' ').slice(0, 2).join(' '),
    score: item.level / 10,
    fullMark: 10,
  }));

  const barData = allItems.map(item => ({
    name: item.skill.length > 15 ? item.skill.slice(0, 15) + '..' : item.skill,
    value: item.level,
    color: item.type === 'strength' ? (item.status === 'excellent' ? '#16A34A' : '#3B82F6') : (item.status === 'critical' ? '#DC2626' : '#F5841F'),
  }));

  const avgScore = allItems.length > 0 ? Math.round(allItems.reduce((a, b) => a + b.level, 0) / allItems.length) : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="overview-section">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Overall Readiness', value: `${data.overall_readiness}%`, color: '#0D9488', icon: Target },
          { label: 'Strengths', value: data.strengths.length, color: '#16A34A', icon: TrendingUp },
          { label: 'Gaps to Close', value: data.gaps.length, color: '#DC2626', icon: AlertTriangle },
          { label: 'Avg Dimension', value: `${avgScore}%`, color: '#8B5CF6', icon: BarChart3 },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="nx-card p-4 text-center" data-testid={`stat-${i}`}>
              <Icon className="w-5 h-5 mx-auto mb-2" style={{ color: stat.color }} />
              <p className="text-2xl font-black" style={{ color: stat.color }}>{stat.value}</p>
              <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>{stat.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Radar + Bar Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Radar */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="nx-card p-5" data-testid="radar-chart">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#8B5CF6' }}>
            Dimension Radar
          </p>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="65%">
                <PolarGrid stroke="var(--t-border)" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 8 }} />
                <Radar name="Score" dataKey="score" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.15} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Bar Chart */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="nx-card p-5" data-testid="bar-chart">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#0D9488' }}>
            Dimension Breakdown
          </p>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <YAxis dataKey="name" type="category" width={85} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <Tooltip
                  contentStyle={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)', borderRadius: 8, fontSize: 11 }}
                  labelStyle={{ color: 'var(--t-text-heading)' }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>
                  {barData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}


// --- STRENGTHS SECTION ---
function StrengthsSection({ data }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="strengths-section">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-5 h-5" style={{ color: '#16A34A' }} />
        <h2 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Core Strengths</h2>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto"
          style={{ background: 'rgba(22,163,106,0.08)', color: '#16A34A' }}>{data.strengths.length} identified</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.strengths.map((item, i) => {
          const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.strong;
          return (
            <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
              className="nx-card p-4 flex gap-3" data-testid={`strength-${i}`}>
              <div className="relative flex-shrink-0">
                <ProgressRing value={item.level} size={56} stroke={4} color={cfg.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{item.skill}</h4>
                  <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>{item.detail}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}


// --- GAPS SECTION ---
function GapsSection({ data }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="gaps-section">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-5 h-5" style={{ color: '#DC2626' }} />
        <h2 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Gaps to Close</h2>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto"
          style={{ background: 'rgba(220,38,38,0.08)', color: '#DC2626' }}>{data.gaps.length} found</span>
      </div>

      {data.gaps.length === 0 ? (
        <div className="nx-card p-8 text-center">
          <CheckCircle className="w-10 h-10 mx-auto mb-3" style={{ color: '#16A34A' }} />
          <p className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>No Critical Gaps</p>
          <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>All dimensions are at or above target levels.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.gaps.map((item, i) => {
            const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.needs_improvement;
            const isExpanded = expanded === i;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="nx-card overflow-hidden" data-testid={`gap-${i}`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : i)}>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <ProgressRing value={item.level} size={56} stroke={4} color={cfg.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded"
                          style={{ background: cfg.bg, color: cfg.color }}>{item.priority}</span>
                        <h4 className="text-sm font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{item.skill}</h4>
                      </div>
                      <p className="text-[11px]" style={{ color: 'var(--t-text-muted)' }}>{item.detail}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        {item.time_required && (
                          <span className="text-[9px] flex items-center gap-1" style={{ color: 'var(--t-text-muted)' }}>
                            <Clock className="w-3 h-3" /> {item.time_required}
                          </span>
                        )}
                        {item.career_impact && (
                          <span className="text-[9px] flex items-center gap-1" style={{ color: '#8B5CF6' }}>
                            <Zap className="w-3 h-3" /> Career Booster
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      style={{ color: 'var(--t-text-muted)' }} />
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                      className="overflow-hidden">
                      <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--t-border)' }}>
                        {/* Action */}
                        <div className="flex items-start gap-2 p-3 rounded-xl mt-3" style={{ background: 'rgba(13,148,136,0.05)', border: '1px solid rgba(13,148,136,0.1)' }}>
                          <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#0D9488' }} />
                          <p className="text-xs font-semibold" style={{ color: '#0D9488' }}>{item.action}</p>
                        </div>

                        {/* Strategy */}
                        {item.strategy && (
                          <div>
                            <p className="text-[9px] font-bold uppercase tracking-wider mb-1.5" style={{ color: '#F5841F' }}>
                              <Target className="w-3 h-3 inline mr-1" />Strategy
                            </p>
                            <p className="text-xs leading-relaxed p-3 rounded-lg" style={{ color: 'var(--t-text-secondary)', background: 'var(--t-bg-section)' }}>
                              {item.strategy}
                            </p>
                          </div>
                        )}

                        {/* How-To Steps */}
                        {item.how_to && (
                          <div>
                            <p className="text-[9px] font-bold uppercase tracking-wider mb-1.5" style={{ color: '#3B82F6' }}>
                              <BookOpen className="w-3 h-3 inline mr-1" />Step-by-Step Guide
                            </p>
                            <div className="space-y-1.5">
                              {item.how_to.split(/\d+\./).filter(Boolean).map((step, si) => (
                                <div key={si} className="flex items-start gap-2.5 p-2 rounded-lg" style={{ background: 'var(--t-bg-section)' }}>
                                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5"
                                    style={{ background: 'rgba(59,130,246,0.1)', color: '#3B82F6' }}>{si + 1}</span>
                                  <p className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{step.trim()}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Resources */}
                        {item.resources?.length > 0 && (
                          <div>
                            <p className="text-[9px] font-bold uppercase tracking-wider mb-1.5" style={{ color: '#16A34A' }}>
                              <Link2 className="w-3 h-3 inline mr-1" />Resources
                            </p>
                            <div className="space-y-1">
                              {item.resources.map((res, ri) => (
                                <a key={ri} href={res.url} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 rounded-lg transition-colors group"
                                  style={{ background: 'var(--t-bg-section)' }}
                                  data-testid={`resource-${i}-${ri}`}>
                                  <ExternalLink className="w-3 h-3 flex-shrink-0" style={{ color: '#16A34A' }} />
                                  <span className="text-xs font-medium" style={{ color: 'var(--t-text-secondary)' }}>{res.name}</span>
                                  <span className="text-[9px] ml-auto px-1.5 py-0.5 rounded"
                                    style={{ background: 'var(--t-bg-card)', color: 'var(--t-text-muted)' }}>{res.type}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Career Impact */}
                        {item.career_impact && (
                          <div className="p-3 rounded-xl" style={{ background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.12)' }}>
                            <p className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: '#8B5CF6' }}>
                              <Zap className="w-3 h-3 inline mr-1" />Career Impact
                            </p>
                            <p className="text-xs" style={{ color: '#8B5CF6' }}>{item.career_impact}</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}


// --- LEARNING ROADMAP SECTION ---
function RoadmapSection({ data }) {
  const gaps = data.gaps || [];
  const strengths = data.strengths || [];

  // Build timeline: gaps first (to close), then strengths to maintain
  const milestones = [];

  gaps.forEach((gap, i) => {
    const steps = gap.how_to ? gap.how_to.split(/\d+\./).filter(Boolean) : [];
    milestones.push({
      phase: i + 1,
      title: gap.skill,
      level: gap.level,
      target: 75,
      priority: gap.priority,
      time: gap.time_required || '1-3 months',
      color: gap.status === 'critical' ? '#DC2626' : '#F5841F',
      steps: steps.slice(0, 4),
      resources: gap.resources || [],
      strategy: gap.strategy,
    });
  });

  // Add a "maintain" phase for top strengths
  const topStrengths = strengths.filter(s => s.status === 'excellent').slice(0, 3);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="roadmap-section">
      <div className="flex items-center gap-2 mb-2">
        <Map className="w-5 h-5" style={{ color: '#8B5CF6' }} />
        <h2 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Learning Roadmap</h2>
        <span className="text-[10px] ml-auto" style={{ color: 'var(--t-text-muted)' }}>
          {milestones.length} milestone{milestones.length !== 1 ? 's' : ''} to reach
        </span>
      </div>

      {milestones.length === 0 ? (
        <div className="nx-card p-8 text-center">
          <Star className="w-10 h-10 mx-auto mb-3" style={{ color: '#16A34A' }} />
          <p className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>All Clear!</p>
          <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>No gaps detected. Focus on maintaining your strengths.</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-px" style={{ background: 'var(--t-border)' }} />

          {milestones.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.12 }}
              className="relative pl-14 pb-8 last:pb-0" data-testid={`milestone-${i}`}>
              {/* Timeline dot */}
              <div className="absolute left-[17px] top-1 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: m.color, boxShadow: `0 0 12px ${m.color}40` }}>
                <span className="text-[8px] font-black text-white">{m.phase}</span>
              </div>

              <div className="nx-card p-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded"
                        style={{ background: `${m.color}12`, color: m.color }}>{m.priority}</span>
                      <h4 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>{m.title}</h4>
                    </div>
                    <p className="text-[10px] mt-0.5 flex items-center gap-2" style={{ color: 'var(--t-text-muted)' }}>
                      <Clock className="w-3 h-3" /> {m.time}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-black" style={{ color: m.color }}>{m.level}%</span>
                      <ArrowRight className="w-3 h-3" style={{ color: 'var(--t-text-muted)' }} />
                      <span className="text-xs font-black" style={{ color: '#16A34A' }}>{m.target}%</span>
                    </div>
                    <p className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>Current → Target</p>
                  </div>
                </div>

                {/* Progress bar: current to target */}
                <div className="relative h-2 rounded-full overflow-hidden mb-4" style={{ background: 'var(--t-border)' }}>
                  <motion.div className="absolute h-full rounded-full"
                    style={{ background: m.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${m.level}%` }}
                    transition={{ duration: 0.8, delay: 0.3 + i * 0.1 }} />
                  <div className="absolute h-full rounded-full opacity-20"
                    style={{ background: '#16A34A', width: `${m.target}%` }} />
                </div>

                {/* Steps */}
                {m.steps.length > 0 && (
                  <div className="space-y-1.5">
                    {m.steps.map((step, si) => (
                      <motion.div key={si} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + si * 0.08 }}
                        className="flex items-start gap-2.5 p-2 rounded-lg" style={{ background: 'var(--t-bg-section)' }}>
                        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ border: '1.5px solid var(--t-border)' }}>
                          <Circle className="w-2 h-2" style={{ color: 'var(--t-text-muted)' }} />
                        </div>
                        <p className="text-[11px]" style={{ color: 'var(--t-text-secondary)' }}>{step.trim()}</p>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Resources inline */}
                {m.resources.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {m.resources.map((r, ri) => (
                      <a key={ri} href={r.url} target="_blank" rel="noopener noreferrer"
                        className="text-[9px] font-semibold px-2 py-1 rounded-lg flex items-center gap-1 transition-colors"
                        style={{ background: 'rgba(22,163,106,0.06)', color: '#16A34A', border: '1px solid rgba(22,163,106,0.1)' }}>
                        <ExternalLink className="w-2.5 h-2.5" /> {r.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}

          {/* Maintain strengths milestone */}
          {topStrengths.length > 0 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: milestones.length * 0.12 + 0.1 }}
              className="relative pl-14" data-testid="maintain-milestone">
              <div className="absolute left-[17px] top-1 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: '#16A34A', boxShadow: '0 0 12px rgba(22,163,106,0.4)' }}>
                <Flag className="w-2.5 h-2.5 text-white" />
              </div>
              <div className="nx-card p-4" style={{ borderLeft: '3px solid #16A34A' }}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#16A34A' }}>
                  Maintain & Leverage
                </p>
                <div className="flex flex-wrap gap-2">
                  {topStrengths.map((s, i) => (
                    <span key={i} className="text-[10px] font-semibold px-2.5 py-1 rounded-lg"
                      style={{ background: 'rgba(22,163,106,0.06)', color: '#16A34A', border: '1px solid rgba(22,163,106,0.1)' }}>
                      {s.skill} — {s.level}%
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </motion.div>
  );
}


// --- ACTION PLAN SECTION ---
function ActionPlanSection({ data }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="actions-section">
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-5 h-5" style={{ color: '#0D9488' }} />
        <h2 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Priority Action Plan</h2>
      </div>
      <div className="space-y-2.5">
        {data.recommendations.map((rec, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.06 }}
            className="nx-card p-4 flex items-start gap-3" data-testid={`action-${i}`}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(13,148,136,0.08)' }}>
              <span className="text-[10px] font-black" style={{ color: '#0D9488' }}>{i + 1}</span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{rec}</p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}


// --- FUTURE SKILLS SECTION ---
function FutureSkillsSection({ data }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="future-section">
      <div className="flex items-center gap-2 mb-2">
        <Rocket className="w-5 h-5" style={{ color: '#8B5CF6' }} />
        <h2 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Future-Proof Skills</h2>
        <span className="text-[10px] ml-auto" style={{ color: 'var(--t-text-muted)' }}>Post-role growth</span>
      </div>

      {(!data.future_skills || data.future_skills.length === 0) ? (
        <div className="nx-card p-8 text-center">
          <GraduationCap className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--t-text-muted)' }} />
          <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Complete more assessments to unlock future skill recommendations.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.future_skills.map((skill, i) => {
            const isExpanded = expanded === i;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="nx-card overflow-hidden" data-testid={`future-${i}`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : i)}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(139,92,246,0.08)' }}>
                      <Brain className="w-5 h-5" style={{ color: '#8B5CF6' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>{skill.skill}</h4>
                      <p className="text-[11px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{skill.description}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-[9px] flex items-center gap-1" style={{ color: 'var(--t-text-muted)' }}>
                          <Clock className="w-3 h-3" /> {skill.time_to_learn}
                        </span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                          style={{ background: 'rgba(139,92,246,0.08)', color: '#8B5CF6' }}>{skill.when}</span>
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      style={{ color: 'var(--t-text-muted)' }} />
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--t-border)' }}>
                        <div className="mt-3">
                          <p className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: '#8B5CF6' }}>Why It Matters</p>
                          <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{skill.why_it_matters}</p>
                        </div>
                        <div className="p-3 rounded-xl" style={{ background: 'rgba(22,163,106,0.05)', border: '1px solid rgba(22,163,106,0.1)' }}>
                          <p className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: '#16A34A' }}>Career Boost</p>
                          <p className="text-xs" style={{ color: '#16A34A' }}>{skill.career_boost}</p>
                        </div>
                        {skill.resources?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {skill.resources.map((url, ri) => (
                              <a key={ri} href={url} target="_blank" rel="noopener noreferrer"
                                className="text-[9px] text-blue-400 hover:text-blue-300 flex items-center gap-1 px-2 py-1 rounded-lg"
                                style={{ background: 'rgba(59,130,246,0.06)' }}>
                                <ExternalLink className="w-2.5 h-2.5" /> {url.length > 40 ? url.slice(0, 40) + '...' : url}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}


// ---- MAIN PAGE ----
export default function GapAnalysisPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [downloading, setDownloading] = useState(false);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      await exportAPI.downloadPDF('/export/gap-analysis-pdf');
      toast.success('PDF downloaded successfully');
    } catch { toast.error('Failed to generate PDF'); }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await dataAPI.gapAnalysis();
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: '#0D9488' }} />
        <span className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Analyzing your career dimensions...</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="gap-analysis-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0D9488' }}>
            Career Intelligence Engine
          </p>
          <h1 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>Gap Analysis</h1>
          <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>
            Strengths, gaps, learning roadmap & action plan
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Readiness badge */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl"
            style={{ background: 'rgba(13,148,136,0.06)', border: '1px solid rgba(13,148,136,0.12)' }}>
            <div className="relative w-9 h-9">
              <svg width={36} height={36} className="-rotate-90">
                <circle cx={18} cy={18} r={15} fill="none" stroke="var(--t-border)" strokeWidth={3} />
                <motion.circle cx={18} cy={18} r={15} fill="none" stroke="#0D9488" strokeWidth={3} strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 15}
                  initial={{ strokeDashoffset: 2 * Math.PI * 15 }}
                  animate={{ strokeDashoffset: 2 * Math.PI * 15 - (data.overall_readiness / 100) * 2 * Math.PI * 15 }}
                  transition={{ duration: 1, ease: 'easeOut' }} />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black" style={{ color: '#0D9488' }}>
                {data.overall_readiness}%
              </span>
            </div>
            <span className="text-[9px] font-bold" style={{ color: 'var(--t-text-muted)' }}>Readiness</span>
          </div>
          <Button onClick={downloadPDF} disabled={downloading}
            className="h-9 px-5 text-[10px] font-bold rounded-xl uppercase tracking-wider"
            style={{ background: '#0D9488', color: '#fff' }}
            data-testid="download-gap-pdf">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Export PDF
          </Button>
        </div>
      </div>

      {/* HubSpot-style tabs */}
      <div className="flex gap-1 border-b mb-6" style={{ borderColor: 'var(--t-border)' }}>
        {SECTIONS.map(s => {
          const Icon = s.icon;
          const isActive = activeSection === s.id;
          let count = null;
          if (s.id === 'strengths') count = data?.strengths?.length;
          if (s.id === 'gaps') count = data?.gaps?.length;
          if (s.id === 'future') count = data?.future_skills?.length;
          return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className="flex items-center gap-1.5 px-4 py-3 -mb-px transition-all"
              style={{
                borderBottom: isActive ? '2px solid #0D9488' : '2px solid transparent',
                color: isActive ? '#0D9488' : 'var(--t-text-muted)',
              }}
              data-testid={`nav-${s.id}`}>
              <Icon className="w-3.5 h-3.5" />
              <span className="text-xs font-bold">{s.label}</span>
              {count != null && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: isActive ? 'rgba(13,148,136,0.1)' : 'var(--t-bg-section)', color: isActive ? '#0D9488' : 'var(--t-text-muted)' }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeSection} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
          {activeSection === 'overview' && <OverviewSection data={data} />}
          {activeSection === 'strengths' && <StrengthsSection data={data} />}
          {activeSection === 'gaps' && <GapsSection data={data} />}
          {activeSection === 'roadmap' && <RoadmapSection data={data} />}
          {activeSection === 'actions' && <ActionPlanSection data={data} />}
          {activeSection === 'future' && <FutureSkillsSection data={data} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
